/**
 * Serialise a `ChatSession` to a markdown blob suitable for stashing
 * in the Asset Locker. The exporter wraps the messages in a tiny
 * header (title, workshop badge, timestamp) and dumps each turn under
 * a `## User` / `## Assistant` heading. Streaming / errored / empty
 * bubbles are dropped — they're not real conversation turns.
 *
 * The current Phase 6 plan stores the markdown as a data URL on the
 * `Upload.url` field. When real GitHub hosting lands, the same
 * serialiser feeds the raw upload step.
 *
 * Source references:
 *   - `useAssetLocker.ts` — `Upload.url` is the GitHub raw URL slot.
 *   - `persistence.ts` — `ChatSession` shape.
 */

import type { ChatSession } from "./persistence";

/** Format an epoch ms timestamp as an ISO-ish, locale-neutral string —
 *  good enough for a markdown export header (we don't need locale
 *  weirdness in a file destined for a code repo). */
function formatTimestamp(ts: number): string {
  return new Date(ts).toISOString().replace("T", " ").slice(0, 19) + " UTC";
}

/** Encode a UTF-8 string as base64 in a way that survives non-ASCII
 *  (the built-in `btoa` only handles Latin-1). */
function utf8Base64(text: string): string {
  try {
    const bytes = new TextEncoder().encode(text);
    let binary = "";
    // chunked to avoid `Maximum call stack size exceeded` on long chats
    for (let i = 0; i < bytes.length; i += 0x8000) {
      const chunk = bytes.subarray(i, i + 0x8000);
      binary += String.fromCharCode(...chunk);
    }
    return btoa(binary);
  } catch {
    // Fallback: drop the high bits. The export is best-effort and
    // shouldn't crash the AssetLocker save flow.
    // eslint-disable-next-line no-control-regex -- intentional ascii-only floor
    return btoa(text.replace(/[^\x00-\xff]/g, "?"));
  }
}

/**
 * Build the markdown body for a session export. Public so tests / the
 * future GitHub uploader can reuse the same serialiser.
 */
export function sessionToMarkdown(session: ChatSession): string {
  const title = session.title || "Chat export";
  const lines: string[] = [];
  lines.push(`# Chat — ${title}`);
  lines.push("");
  lines.push(
    `${session.workshopId} · ${formatTimestamp(session.updatedAt || session.createdAt)}`,
  );
  lines.push("");

  const turns = session.messages.filter(
    (m) => !m.streaming && !m.error && m.text.trim().length > 0,
  );
  for (const turn of turns) {
    lines.push(turn.role === "user" ? "## User" : "## Assistant");
    lines.push(turn.text.trim());
    lines.push("");
  }

  return lines.join("\n");
}

/** Wrap a markdown export as a `data:text/markdown;base64,…` URL.
 *  Browsers render that inline as plain text, which is enough until
 *  Phase 6 swaps in a GitHub raw URL. */
export function sessionToDataUrl(session: ChatSession): string {
  const md = sessionToMarkdown(session);
  return `data:text/markdown;base64,${utf8Base64(md)}`;
}
